import styled from 'styled-components'

import dynamic from 'next/dynamic'
// import Autocomplete from 'src/components/Search/Autocomplete'
// Lazy import autocomplete
const Autocomplete = dynamic(() => import('src/components/Search/Autocomplete'), {
  ssr: false,
})

import SingleLineInput from 'src/components/form/SingleLineInput'
import Input from 'src/components/form/Input'
import Select from 'src/components/form/Select'
import { StyledButton, StyledLinkButton } from 'src/styles/Button'

import { useRxSearch } from 'src/utils/rx/useRxSearch'
import SvgDiscountIcon from './discountIcon'
import SvgAddressIcon from './addressIcon'
import SvgPhoneIcon from './phoneIcon'
import SvgToggleOpenIcon from './toggleOpenIcon'
import PharmacyListMap from './PharmacyListMap'
import Loader from 'src/components/Loader'

const RxSearch = ({ className = '' }) => {
  const {
    formState,
    onChange,
    onUpdateList,
    options,
    popularDrugs,
    pharmacies,
    isLoadingComponents,
    isLoadingPharmacyList,
    selectedDrugName,
    setSelectedDrugName,
    getSources,
    errorMessage,
    pharmacyExtraInfoIsOpen,
    togglePharmacyOpen,
  } = useRxSearch()

  const buildMapLink = (item) => {
    if (
      /* if we're on iOS, open in Apple Maps */
      navigator?.platform?.indexOf?.('iPhone') != -1 ||
      navigator?.platform?.indexOf?.('iPad') != -1 ||
      navigator?.platform?.indexOf?.('iPod') != -1
    ) {
      return `maps://maps.google.com/maps?daddr=${item.Lat},${item.Long}&amp;ll=`
    } else {
      return `https://maps.google.com/maps?daddr=${item.Lat},${item.Long}&amp;ll=`
    }
  }

  const onSubmitSearch = (e) => e.preventDefault()

  return (
    <div className={className}>
      {!selectedDrugName && (
        <MedicationSearchForm onSubmit={onSubmitSearch}>
          <SearchHeading>
            <span>
              <SvgDiscountIcon /> Discover Your Savings
            </span>
          </SearchHeading>

          <MedicationSearch>
            <ZipcodeInput>
              <Input
                placeholder="Enter zipcode"
                name="zipcode"
                value={formState.zipcode}
                onChange={onChange('zipcode')}
              />
            </ZipcodeInput>

            <MedicationInput>
              <Autocomplete
                placeholder="Enter medication..."
                openOnFocus={true}
                defaultActiveItemid={0}
                getSources={getSources}
              />
            </MedicationInput>

            <UseLocation>&nbsp;</UseLocation>

            <FindLowerPrices>
              <StyledButton>Find Lowest Price</StyledButton>
            </FindLowerPrices>

            <DrugList>
              {popularDrugs.map((drug, index) => (
                <StyledButton
                  variant="secondary"
                  key={index}
                  onClick={() => setSelectedDrugName(drug.CallerName)}
                  type="button"
                >
                  {drug.DrugName || 'n/a'}
                </StyledButton>
              ))}
            </DrugList>
          </MedicationSearch>
        </MedicationSearchForm>
      )}

      {selectedDrugName && (
        <PrescriptionDiscountsForm onSubmit={onUpdateList}>
          <SearchHeading>
            <span>
              <SvgDiscountIcon />
              Prescription Discounts
            </span>
            <StyledLinkButton onClick={() => setSelectedDrugName(false)} type="button">
              - Lookup Another Prescription
            </StyledLinkButton>
          </SearchHeading>

          {isLoadingComponents && (
            <>
              <p>We’re searching for best prices, one moment please...</p>
              <Loader color="#ccc" />
            </>
          )}

          {!isLoadingComponents && (
            <div>
              <PrescriptionFilters>
                <div className="ps-select ps-select--medication">
                  <Select
                    label="Medication"
                    name="medication"
                    value={formState.medication?.id}
                    options={options.medications}
                    onChange={onChange('medication')}
                  />
                </div>

                <div className="ps-select ps-select--form">
                  <Select
                    label="Form"
                    name="dose"
                    value={formState.dose?.id}
                    onChange={onChange('dose')}
                    options={options.doses}
                  />
                </div>

                <div className="ps-select ps-select--dosage">
                  <Select
                    label="Dosage"
                    name="strength"
                    value={formState.strength?.id}
                    onChange={onChange('strength')}
                    options={options.strength}
                  />
                </div>

                <div className="ps-select ps-select--quantity">
                  <Select
                    label="Quantity"
                    name="quantity"
                    value={formState.quantity?.id}
                    onChange={onChange('quantity')}
                    options={options.quantity}
                  />
                </div>

                <div className="ps-select ps-select--zipcode">
                  <SingleLineInput
                    label="Zipcode"
                    placeholder="Zipcode"
                    name="zipcode"
                    value={formState.zipcode}
                    onChange={onChange('zipcode')}
                    height="40px"
                    labelProps={{
                      labelSpace: '1rem',
                      mobileLabelSpace: '0',
                      height: '40px',
                    }}
                    labelSpace="1rem"
                    mobileLabelSpace="0"
                  />
                </div>

                <div className="ps-select ps-select--update">
                  <StyledButton type="submit">Update</StyledButton>
                </div>
              </PrescriptionFilters>

              {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

              {isLoadingPharmacyList && (
                <>
                  <p>Loading List of Pharmacies.</p>
                  <Loader color="#ccc" />
                </>
              )}

              {!isLoadingPharmacyList && pharmacies && pharmacies.length ? (
                <Pharmacies>
                  <PharmacyList>
                    <h3>Pharmacies</h3>

                    <PharmacyItemList>
                      {pharmacies.map((item, i) => (
                        <PharmacyItem key={i}>
                          <header>
                            <Number>{i + 1}</Number>
                            <h4>{item.PharmacyName}</h4>
                          </header>

                          <section>
                            <div>
                              <Miles>{item.Distance}</Miles>
                              <Sep>-</Sep>
                              <PriceSlashed>
                                ${item.retailPrice} retail{' '}
                                <span>{item.percentSaved}%</span>
                              </PriceSlashed>
                            </div>

                            <div>
                              <Price>${item.yourPrice}</Price>
                              <TogglePharmacyExtraInfoButton
                                type="button"
                                onClick={() => togglePharmacyOpen(item.NPI)}
                              >
                                <SvgToggleOpenIcon
                                  isOpen={pharmacyExtraInfoIsOpen?.[item.NPI]}
                                />
                              </TogglePharmacyExtraInfoButton>
                            </div>
                          </section>

                          <AdditionalPharmacyInfo
                            isOpen={pharmacyExtraInfoIsOpen?.[item?.NPI]}
                          >
                            <p>
                              <SvgAddressIcon />
                              <a
                                href={buildMapLink(item)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {item.Address}, {item.City} {item.State}
                              </a>
                            </p>

                            {item.PBMPhone && (
                              <p>
                                <SvgPhoneIcon />{' '}
                                <a href={`tel:${item.PBMPhone}`}>{item.PBMPhone}</a>
                              </p>
                            )}
                          </AdditionalPharmacyInfo>
                        </PharmacyItem>
                      ))}
                    </PharmacyItemList>
                  </PharmacyList>

                  <PharmacyMap>
                    <PharmacyListMap
                      pharmacies={pharmacies}
                      togglePharmacyOpen={togglePharmacyOpen}
                    />
                  </PharmacyMap>
                </Pharmacies>
              ) : null}
            </div>
          )}
        </PrescriptionDiscountsForm>
      )}
    </div>
  )
}

const TogglePharmacyExtraInfoButton = styled.button`
  background: none;
  border: unset;
  padding: 4px;
  margin: 0;
`

const AdditionalPharmacyInfo = styled.div`
  display: ${({ isOpen = false }) => (isOpen ? 'flex' : 'none')};
  flex-direction: column;
  gap: 12px;
  padding-top: 24px;

  p {
    margin: 0;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 12px;
  }
`

const ErrorMessage = styled.p`
  color: red;
`

const SearchHeading = styled.h2`
  margin: 0;
  padding: 24px 0 32px 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    display: flex;
    align-items: center;
  }

  > span {
    display: flex;
    align-items: center;
  }

  ${SvgDiscountIcon} {
    margin-right: 12px;
  }

  ${StyledLinkButton} {
    margin: 0 0 0 12px;
    position: relative;
    top: 2px;
    padding: 0;
    font-size: 70%;
    font-weight: normal;
  }
`
const Miles = styled.span``
const Sep = styled.span``
const PriceSlashed = styled.s`
  span {
    display: none;
  }
`
const Price = styled.strong``

const Number = styled.div`
  background-color: rgba(8, 138, 178, 1);
  color: #fff;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  padding: 2px;
  font-size: 90%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const PharmacyItemList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
  width: 320px;
`

const PharmacyItem = styled.li`
  border-bottom: 1px solid rgba(227, 235, 237, 1);
  padding: 12px 0;
  width: 100%;

  header {
    display: flex;
    width: 100%;
    align-items: center;

    ${Number} {
      margin-right: 12px;
    }
  }

  section {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    color: rgba(107, 114, 128, 1);

    ${Sep} {
      padding: 0 12px;
    }

    ${Price} {
      color: rgba(31, 41, 55, 1);
    }
  }

  ${TogglePharmacyExtraInfoButton} {
    margin-left: 12px;
  }
`

const Pharmacies = styled.div`
  display: flex;
  align-items: flex;
  justify-content: space-between;
  gap: 42px;
  margin: 24px 0;
  flex-direction: column;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    flex-direction: row;
  }
`

const PharmacyList = styled.div``
const PharmacyMap = styled.div`
  background-color: #ddd;
  width: 100%;

  #map {
    width: 100%;
    height: 100%;
    min-height: 450px;
  }
`

const ZipcodeInput = styled.div``
const MedicationInput = styled.div``
const DrugList = styled.div`
  overflow-x: scroll;
  overflow-y: hidden;
  display: flex;

  ${StyledButton} {
    margin-left: 12px;
    white-space: nowrap;

    &:first-child {
      margin-left: 0;
    }
  }
`
const UseLocation = styled.div``
const FindLowerPrices = styled.div``

const MedicationSearch = styled.div`
  display: grid;
  grid-gap: 2%;
  grid-template-columns: 49% 49%;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: 24% 24% 24% 24%;

    grid-template-areas:
      'zipcode medication medication submit'
      'uselocation druglist druglist druglist';
  }

  grid-template-areas:
    'medication medication'
    'zipcode submit'
    'druglist druglist';

  ${ZipcodeInput} {
    grid-area: zipcode;
    padding-top: 10px;

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
      padding-top: 0;
    }
  }

  ${MedicationInput} {
    grid-area: medication;
  }

  ${FindLowerPrices} {
    grid-area: submit;
    align-self: stretch;
    justify-self: stretch;
    padding-top: 10px;

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
      padding-top: 0;
    }

    ${StyledButton} {
      width: 100%;
      height: 100%;

      @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        height: 100%;
      }
    }
  }

  ${UseLocation} {
    grid-area: uselocation;
  }

  ${DrugList} {
    padding: 12px 0 30px 0;
    grid-area: druglist;

    ${StyledButton} {
      height: 32px;
    }
  }
`
const PrescriptionFilters = styled.div`
  display: grid;
  grid-gap: 2%;
  align-items: end;
  padding-bottom: 24px;

  grid-template-columns: 48% 48%;
  grid-template-areas:
    'medication medication'
    'form form'
    'dosage dosage'
    'quantity zipcode'
    'update update';

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-gap: 1%;
    grid-template-columns: 35% 12% 22% 8% 10% 8%;
    grid-template-areas: 'medication form dosage quantity zipcode update';

    .ps-select {
      padding: 0;

      label,
      h3 {
        margin-bottom: 1rem;
      }
    }
  }

  .ps-select {
    padding: 4px 0;

    label,
    h3 {
      margin: 0;
    }
  }

  .ps-select--medication {
    grid-area: medication;
  }
  .ps-select--form {
    grid-area: form;
  }
  .ps-select--dosage {
    grid-area: dosage;
    width: 100%;
  }
  .ps-select--quantity {
    grid-area: quantity;
  }
  .ps-select--zipcode {
    grid-area: zipcode;
  }
  .ps-select--update {
    grid-area: update;

    ${StyledButton} {
      width: 100%;
    }
  }
`

const MedicationSearchForm = styled.form``
const PrescriptionDiscountsForm = styled.form``

export default styled(RxSearch)`
  margin: 12px 0;

  ${ZipcodeInput} {
    input {
      height: 46px;
    }
  }
`
